import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { holidayBlock } from './calendarModel';
import { fmtLongDate, fmtRange, weekdayLong, fmtDays, fmtHours } from './calendarFormat';

// Güne gelince kısa ipucu (spec §3.5) — yalnız fareli cihazda; document.body'ye portal (kart taşmasın).
export default function DayHoverTip({ day, anchor, entry, index, today }) {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!ref.current || !anchor) return;
    const r = anchor.getBoundingClientRect();
    const tw = ref.current.offsetWidth;
    const th = ref.current.offsetHeight;
    let left = r.left + r.width / 2 - tw / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - tw - 8));
    let top = r.top - th - 8;
    if (top < 64) top = r.bottom + 8;
    setPos({ left, top });
  }, [anchor, day.date]);

  const { cell } = day;
  const rows = [];
  if (cell.holiday) {
    const block = holidayBlock(index, day.date);
    rows.push(
      <Row key="h">
        <Dot $kind="holiday" />
        <div>
          <T1>{cell.holiday.name}<Tag $kind="holiday">{t(cell.holiday.isHalfDay ? 'myCalendar.tag.halfDay' : 'myCalendar.tag.holiday')}</Tag></T1>
          {block && <T2>{fmtRange(t, block.start, block.end)}</T2>}
        </div>
      </Row>,
    );
  }
  cell.leaves.forEach((l) => {
    const weight = entry?.leaves.find((x) => x.leave.id === l.id)?.weight;
    const span = l.isHourly
      ? `${l.startTime} – ${l.endTime} · ${fmtHours(t, i18n.language, l.cost)}`
      : `${fmtRange(t, l.startDate, l.lastLeaveDate)} · ${fmtDays(t, i18n.language, l.cost)}`;
    let note = null;
    if (!l.isHourly && weight === 0) {
      note = entry?.holiday && !entry.holiday.isHalfDay ? t('myCalendar.tip.holidayNotDeducted') : t('myCalendar.tip.offDayNotDeducted');
    }
    rows.push(
      <Row key={l.id}>
        <Dot $kind={l.status} />
        <div>
          <T1>{l.leaveVariationName}<Tag $kind={l.status}>{t(`myCalendar.tag.${l.status}`)}</Tag></T1>
          <T2>{span}</T2>
          {note && <T2>{note}</T2>}
        </div>
      </Row>,
    );
  });

  return createPortal(
    <Tip ref={ref} data-portal-layer="calendar-tip" style={pos || { left: -9999, top: -9999 }} $show={!!pos} role="tooltip">
      <Head>
        {fmtLongDate(t, day.date)}, {weekdayLong(t, day.date)}
        {day.date === today ? ` · ${t('myCalendar.tip.today')}` : ''}
      </Head>
      {rows.length
        ? rows
        : <Empty>{day.isWeekend ? t('myCalendar.tip.weekend') : t('myCalendar.tip.noRecord')}</Empty>}
    </Tip>,
    document.body,
  );
}

const Tip = styled.div`
  position: fixed;
  z-index: 1400;
  pointer-events: none;
  min-width: 220px;
  max-width: 280px;
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 10px 32px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.16), 0 0 0 1px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.12s;
`;

const Head = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;

  & + & {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
  }
`;

export const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 4px;
  flex-shrink: 0;
  box-sizing: border-box;
  ${({ $kind, theme }) => {
    const c = theme.colors.calendar;
    if ($kind === 'holiday') return `background: ${c.holidayBg}; box-shadow: inset 0 0 0 1.5px ${c.holidayText};`;
    if ($kind === 'approved') return `background: ${c.approved};`;
    return `background: ${c.waitingBg}; border: 1.5px dashed ${c.waitingBorder};`;
  }}
`;

const T1 = styled.div`
  font-weight: 700;
`;

const T2 = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
  margin-top: 2px;
`;

export const Tag = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 8px;
  border-radius: 999px;
  margin-left: 6px;
  vertical-align: 1px;
  ${({ $kind, theme }) => {
    const c = theme.colors.calendar;
    if ($kind === 'holiday') return `background: ${c.holidayBg}; color: ${c.holidayText};`;
    if ($kind === 'approved') return `background: ${c.approvedTagBg}; color: ${c.approvedSoftText};`;
    return `background: ${c.waitingBg}; color: ${c.waitingText};`;
  }}
`;

const Empty = styled.div`
  color: ${({ theme }) => theme.colors.calendar.faint};
  font-size: 12px;
`;
